/**
 * Chapter020.js — Hazardous Substances / Dangerous Goods Register (OHSE 020)
 * Migrated from SWMS Platform Chapter020.tsx → JavaScript
 *
 * Props:
 *   data     {array}  hazardous_substance_register from swmsData
 *   onChange {fn}     (newEntries) => void
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-xs bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const thCls = 'px-2 py-2 text-left text-xs font-semibold text-[#1a1a2e] uppercase tracking-wide bg-[#f0ede6]';

/** Mutually exclusive Yes/No checkboxes. value: true | false | null */
function YesNo({ value, onChange }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <label className="flex items-center gap-0.5 cursor-pointer">
        <input
          type="checkbox"
          checked={value === true}
          onChange={e => onChange(e.target.checked ? true : null)}
          className="w-3.5 h-3.5 rounded border-[#e0dbd0] accent-[#c9a84c] cursor-pointer"
        />
        <span className="text-[10px] font-semibold text-[#1a1a2e]">Yes</span>
      </label>
      <label className="flex items-center gap-0.5 cursor-pointer">
        <input
          type="checkbox"
          checked={value === false}
          onChange={e => onChange(e.target.checked ? false : null)}
          className="w-3.5 h-3.5 rounded border-[#e0dbd0] accent-[#c9a84c] cursor-pointer"
        />
        <span className="text-[10px] font-semibold text-[#1a1a2e]">No</span>
      </label>
    </div>
  );
}

export default function Chapter020({ data = [], onChange }) {
  const addEntry = useCallback(() => {
    onChange([...data, {
      product_name: '', application: '', quantity: '',
      product_labelled: null, msds: null, classified_hazardous: null,
    }]);
  }, [data, onChange]);

  const removeEntry = useCallback((idx) => {
    onChange(data.filter((_, i) => i !== idx));
  }, [data, onChange]);

  const updateField = useCallback((idx, field, value) => {
    const next = [...data];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 020</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">
          Hazardous Substances / Dangerous Goods Register
        </h2>
      </div>

      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-[#f0ede6]">
          <h3 className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
            Hazardous Substances Register
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Register of all hazardous substances and dangerous goods on site</p>
        </div>

        <div className="p-4">
          <p className="text-sm text-[#1a1a2e] leading-relaxed mb-4">
            The following hazardous substances exist in the work place. A copy of the MSDS has been forwarded
            to the person responsible for First Aid.
          </p>

          {data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <p className="text-sm text-gray-400 mb-3">No hazardous substances registered yet.</p>
              <button type="button" onClick={addEntry}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
                <Plus className="w-4 h-4" /> Add Substance
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b-2 border-[#1a1a2e]">
                      <th rowSpan={2} className={thCls + ' w-[160px]'}>Product Name</th>
                      <th rowSpan={2} className={thCls + ' w-[130px]'}>Application</th>
                      <th rowSpan={2} className={thCls + ' w-[80px]'}>Quantity</th>
                      <th colSpan={2} className={thCls + ' text-center border-b border-[#e0dbd0]'}>
                        Product Labelled
                      </th>
                      <th colSpan={2} className={thCls + ' text-center border-b border-[#e0dbd0]'}>
                        MSDS
                      </th>
                      <th colSpan={2} className={thCls + ' text-center border-b border-[#e0dbd0]'}>
                        Classified Hazardous
                      </th>
                      <th className={thCls + ' w-[36px]'}></th>
                    </tr>
                    <tr className="border-b border-[#1a1a2e]">
                      {['Yes','No','Yes','No','Yes','No'].map((lbl, i) => (
                        <th key={i} className="px-1 py-1 text-center text-[10px] font-semibold text-[#1a1a2e] bg-[#f0ede6]">
                          {lbl}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((entry, idx) => (
                      <tr key={idx} className={`border-b border-[#e0dbd0] ${idx % 2 === 0 ? 'bg-[#faf9f6]' : 'bg-white'}`}>
                        <td className="px-2 py-2">
                          <input type="text" value={entry.product_name || ''} placeholder="Product name"
                            onChange={e => updateField(idx, 'product_name', e.target.value)} className={inputCls} />
                        </td>
                        <td className="px-2 py-2">
                          <input type="text" value={entry.application || ''} placeholder="Application"
                            onChange={e => updateField(idx, 'application', e.target.value)} className={inputCls} />
                        </td>
                        <td className="px-2 py-2">
                          <input type="text" value={entry.quantity || ''} placeholder="e.g. 5L"
                            onChange={e => updateField(idx, 'quantity', e.target.value)} className={inputCls} />
                        </td>
                        <td colSpan={2} className="px-1 py-2 text-center">
                          <YesNo value={entry.product_labelled} onChange={v => updateField(idx, 'product_labelled', v)} />
                        </td>
                        <td colSpan={2} className="px-1 py-2 text-center">
                          <YesNo value={entry.msds} onChange={v => updateField(idx, 'msds', v)} />
                        </td>
                        <td colSpan={2} className="px-1 py-2 text-center">
                          <YesNo value={entry.classified_hazardous} onChange={v => updateField(idx, 'classified_hazardous', v)} />
                        </td>
                        <td className="px-1 py-2 text-center">
                          <button type="button" onClick={() => removeEntry(idx)}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-3 bg-[#f8f7f4] border border-[#e0dbd0] rounded text-sm text-[#1a1a2e] leading-relaxed">
                <span className="font-semibold">If YES Ticked:</span>{' '}
                The risks and control measures associated with the use of the product/substance and the precautions
                for its use are outlined in the Safe Work Method Statement.
              </div>

              <div className="flex justify-center mt-4">
                <button type="button" onClick={addEntry}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
                  <Plus className="w-4 h-4" /> Add Substance
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
